/* eslint-disable fp/no-mutation */
/* eslint-disable no-shadow */
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Typography,
} from '@mui/material'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import QuizIcon from '@mui/icons-material/Quiz'
import { ReactElement, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import axios from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import useSession from 'src/authentication/hooks/use-session'
import {
  classIDChanged,
  courseIDChanged,
  fetchCertificateAndStatus,
  fetchClassCompletionStatus,
  fetchClasses,
  fetchCourseCompletionStatus,
  fetchCourses,
  fetchProgress,
  fetchQuiz,
  fetchStream,
  fetchProfileStatus,
  progressResetted,
  selectCertificateAndStatus,
  selectCertificateAndStatusStatus,
  selectClassByID,
  selectClassCompletionStatus,
  selectClassCompletionStatusStatus,
  selectClassID,
  selectClassesByCourseID,
  selectClassesStatus,
  selectCourseByID,
  selectCourseCompletionStatus,
  selectCourseCompletionStatusStatus,
  selectCourseID,
  selectCoursesStatus,
  selectProgressStatus,
  selectQuizStatus,
  selectStreamItems,
  selectStreamStatus,
  selectProfileStatus,
  selectProfileStatusStatus,
} from './classroom.slice'
import StepContent from './components/step-content'
import ClassroomStepper from './components/classroom-stepper'
import { CertificateIcon } from '../core/icons'

let boundTreeFreeCourseIDs = [
  'b4995e14-8480-4b5d-99a7-aa814b0c45d1',
  '039c7217-4172-474f-b4c1-f47bcab67310',
  '0646715f-54ac-45d0-a649-a72b6cacda60',
  'b85cd107-e5f1-4ecb-a0dd-e9e3fda4ad0d',
  '3bd2fcc3-80f7-4486-9b72-8b2c7f652148',

  'ca1ddd61-6a0c-43ff-902d-21ab2a791f26',
  '348d5270-dfdb-408d-a255-4e91ba79d99d',
  '0c458a7b-7def-47f6-998d-7179a350fdc9',
  'dce6541e-2d57-45a8-a888-6cc41fb07ea4',
  '49391a29-840b-4c31-b51e-965dd27279bd',
]

export default (): ReactElement => {
  let { courseID: courseIDParam, classID: classIDParam } = useParams()
  let session = useSession()
  let navigate = useNavigate()
  let classID = useAppSelector(selectClassID)
  let courseID = useAppSelector(selectCourseID)
  let classesInCourse = useAppSelector(selectClassesByCourseID(courseID))
  let dispatch = useAppDispatch()
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let classesStatus = useAppSelector(selectClassesStatus)
  let progressStatus = useAppSelector(selectProgressStatus)
  let streamStatus = useAppSelector(selectStreamStatus)
  let quizStatus = useAppSelector(selectQuizStatus)
  let courseCompletionStatusStatus = useAppSelector(
    selectCourseCompletionStatusStatus,
  )
  let classCompletionStatusStatus = useAppSelector(
    selectClassCompletionStatusStatus,
  )
  let classCompletionStatus = useAppSelector(selectClassCompletionStatus)
  let certificateAndStatusStatus = useAppSelector(
    selectCertificateAndStatusStatus,
  )
  let courseCompletionStatus = useAppSelector(selectCourseCompletionStatus)
  let streamItems = useAppSelector(selectStreamItems)
  let $class = useAppSelector(selectClassByID(classID))
  let course = useAppSelector(selectCourseByID(courseID))
  let certificateAndStatus = useAppSelector(selectCertificateAndStatus)
  let profileStatus = useAppSelector(selectProfileStatus)
  let profileStatusStatus = useAppSelector(selectProfileStatusStatus)

  let [downloading, setDownloading] = useState(false)
  let [restarting, setRestarting] = useState(false)
  let [surveyConfirmOpen, setSurveyConfirmOpen] = useState(false)

  let downloadCertificate = () => {
    axios({
      url: `/api/v2/classroom/certificate${certificateAndStatus?.certificateID ? `?certificateID=${certificateAndStatus?.certificateID}` : ''}`,
      method: 'GET',
    }).then((response) => {
      axios
        .get(response.data, {
          responseType: 'blob',
        })
        .then((response) => {
          let url = window.URL.createObjectURL(new Blob([response.data]))
          let link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `${course?.name}.pdf`)
          document.body.appendChild(link)
          link.click()
          setDownloading(false)
        })
        .catch(() => {
          setDownloading(false)
          // eslint-disable-next-line no-alert
          alert('Error downloading PDF')
        })
    })
  }

  let restartCourse = () => {
    axios
      .post('/api/v2/classroom/stream-event/course/restart', {
        courseID,
      })
      .then(() => {
        setRestarting(true)
        return new Promise<void>((resolve) => {
          setTimeout(() => {
            resolve()
          }, 3000)
        })
      })
      .then(() => {
        setRestarting(false)
        dispatch(progressResetted())
        dispatch(classIDChanged(classIDParam ?? ''))
        dispatch(courseIDChanged(courseIDParam ?? ''))
        if (classesInCourse.length > 1) {
          navigate(`/self-paced/course/${courseID}`)
        }
      })
      .catch(() => {
        setRestarting(false)
        // eslint-disable-next-line no-alert
        alert('Error restarting course')
      })
  }

  let accessDenied =
    courseID &&
    session?.boundTreeStatus === 'limited' &&
    !boundTreeFreeCourseIDs.includes(courseID)

  useEffect(() => {
    if (accessDenied) {
      navigate('/self-paced')
    }
  }, [courseID, accessDenied])

  useEffect(() => {
    if (progressStatus === 'uninitialized' && classID)
      dispatch(fetchProgress(classID))
  }, [progressStatus, classID])

  useEffect(() => {
    if (streamStatus === 'uninitialized' && !!classID)
      dispatch(fetchStream(classID))
  }, [streamStatus, classID])

  useEffect(() => {
    if (coursesStatus === 'uninitialized') dispatch(fetchCourses())
  }, [coursesStatus])

  useEffect(() => {
    if (classesStatus === 'uninitialized') dispatch(fetchClasses())
  }, [classesStatus])

  useEffect(() => {
    if (courseID && courseCompletionStatusStatus === 'uninitialized')
      dispatch(fetchCourseCompletionStatus(courseID))
  }, [courseID, courseCompletionStatusStatus])

  useEffect(() => {
    if (classID && classCompletionStatusStatus === 'uninitialized')
      dispatch(fetchClassCompletionStatus(classID))
  }, [classID, classCompletionStatusStatus])

  useEffect(() => {
    if (
      certificateAndStatusStatus === 'uninitialized' &&
      courseID &&
      session?.userId
    )
      dispatch(fetchCertificateAndStatus({ courseID, userID: session?.userId }))
  }, [certificateAndStatusStatus, courseID, session?.userId])

  useEffect(() => {
    if (classID && quizStatus === 'uninitialized') dispatch(fetchQuiz(classID))
  }, [classID, quizStatus])

  useEffect(() => {
    if (classCompletionStatus.status === 'restarted') {
      axios.post('/api/v2/classroom/stream-event/class', {
        classID,
        status: 'started',
      })
    }
  }, [classCompletionStatus.status])

  useEffect(() => {
    if (profileStatusStatus === 'uninitialized') dispatch(fetchProfileStatus())
  }, [profileStatusStatus])

  return (
    <>
      <Box sx={{ p: 2 }}>
        <div className="container-fluid">
          {
            /* Profile information incomplete, make them complete profile */
            profileStatus.length > 0 ? (
              <Paper
                style={{
                  padding: 16,
                  backgroundImage: `url(${import.meta.env.VITE_CLOUDFRONT_URL}/public/${course?.imageName})`,
                  backgroundSize: 'cover',
                  backgroundPosition: '0px 48%',
                  boxShadow: 'inset 0 0 0 1000px rgba(37, 43, 66, 0.94)',
                }}
              >
                <Typography variant="h4" sx={{ fontFamily: 'smileAndWave' }}>
                  {course?.name}
                </Typography>
                <Typography sx={{ my: 1 }}>
                  Before taking this course, please complete your profile
                  information. In order for us to submit your CE to CAPCE and
                  other registries, you will need to fill out the following
                  info:
                </Typography>
                <ul>
                  {profileStatus.map((field) => (
                    <li key={field}>{field}</li>
                  ))}
                </ul>
                <Button
                  sx={{ mt: 1 }}
                  variant="contained"
                  onClick={() => navigate('/profile')}
                >
                  Go to Profile
                </Button>
              </Paper>
            ) : /* Course Complete WITH Certificate, make them restart */
            courseCompletionStatus.status === 'completed' &&
              certificateAndStatus.certificateStatus === 'hasCertificate' ? (
              <Container maxWidth="lg">
                <div
                  style={{
                    backgroundImage: `url("${import.meta.env.VITE_CLOUDFRONT_URL}/public/${(streamItems as any)?.[0]?.imageName}")`,
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                  }}
                >
                  <div className="p-4">
                    <Typography
                      sx={{ fontFamily: 'smileAndWave' }}
                      variant="h4"
                    >
                      {$class?.name}
                    </Typography>
                    <Typography variant="body1" style={{ marginBottom: 60 }}>
                      Congratulations, you have completed this course and have
                      received a certificate of completion. You may download
                      your certificate, or restart the course for a refresher.
                      Restarting the course will count as new CE credits.
                    </Typography>
                    <Box
                      sx={{
                        m: 4,
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 2,
                      }}
                    >
                      <Paper
                        style={{
                          backgroundImage: `url(${import.meta.env.VITE_CLOUDFRONT_URL}/public/${course?.imageName})`,
                          backgroundSize: 'cover',
                          backgroundPosition: '0px 48%',
                          boxShadow:
                            'inset 0 0 0 1000px rgba(37, 43, 66, 0.94)',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            p: 1.5,
                          }}
                        >
                          <Typography
                            gutterBottom
                            variant="subtitle1"
                            component="div"
                          >
                            <CertificateIcon /> {course?.name}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            {course?.displayName} / Hours: {course?.length}
                          </Typography>
                          <div className="flex flex-row-reverse">
                            <Button
                              disabled={downloading}
                              onClick={downloadCertificate}
                            >
                              Download Certificate
                            </Button>
                          </div>
                        </Box>
                      </Paper>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                          p: 1.5,
                        }}
                      >
                        <Typography
                          gutterBottom
                          variant="subtitle1"
                          component="div"
                        >
                          <RestartAltIcon /> Restart Course
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          Once you restart you will need to manually complete
                          each step of the full course again.
                        </Typography>
                        <div className="flex flex-row-reverse">
                          <Button
                            disabled={restarting}
                            variant="contained"
                            onClick={() => {
                              restartCourse()
                            }}
                          >
                            {restarting ? 'Restarting...' : 'Restart Course'}
                          </Button>
                        </div>
                      </Box>
                    </Box>
                  </div>
                </div>
              </Container>
            ) : /* Course Complete WITHOUT Certificate, send them to survey */
            courseCompletionStatus.status === 'completed' &&
              certificateAndStatus.certificateStatus === 'noCertificate' ? (
              <Container maxWidth="lg">
                <div
                  style={{
                    backgroundImage: `url("${import.meta.env.VITE_CLOUDFRONT_URL}/public/${(streamItems as any)?.[0]?.imageName}")`,
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                  }}
                >
                  <div className="p-4">
                    <Typography
                      sx={{ fontFamily: 'smileAndWave' }}
                      variant="h4"
                    >
                      {$class?.name}
                    </Typography>
                    <Typography variant="body1" style={{ marginBottom: 60 }}>
                      Congratulations, you have completed this course but you
                      still need to complete your CE survey to receive your
                      certificate. Once you have your certicicate you may come
                      back here at a later time and restart your progress for a
                      refresher.
                    </Typography>
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                    >
                      <Typography
                        gutterBottom
                        variant="subtitle1"
                        component="div"
                      >
                        <QuizIcon /> Survey
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        By taking the survey your CE will be submittted to the
                        appropriate registries and you will receive your
                        certificate.
                      </Typography>
                      <div className="flex flex-row-reverse">
                        <Button
                          variant="contained"
                          onClick={() => setSurveyConfirmOpen(true)}
                        >
                          Go to Survey
                        </Button>
                      </div>
                      <Dialog
                        open={surveyConfirmOpen}
                        onClose={() => setSurveyConfirmOpen(false)}
                      >
                        <DialogTitle>Complete Your CE Survey</DialogTitle>
                        <DialogContent>
                          <DialogContentText>
                            This course was previously completed but the CE
                            survey was not finished. Completing the survey is
                            required to be submitted to the national registry
                            and receive your certificate. Due to compliance
                            requirements, the survey cannot be completed on
                            quizzes passed too long ago. If you receive a quiz
                            completion error after submitting the survey, please
                            contact support.
                          </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                          <Button onClick={() => setSurveyConfirmOpen(false)}>
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            onClick={() =>
                              navigate(
                                `/classroom/survey/${courseID}/${classID}`,
                              )
                            }
                          >
                            Continue to Survey
                          </Button>
                        </DialogActions>
                      </Dialog>
                    </Box>
                  </div>
                </div>
              </Container>
            ) : (
              /* Course not complete. User can interact with content */
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-9">
                  <StepContent />
                </div>
                <div className="col-span-12 md:col-span-3">
                  <ClassroomStepper />
                </div>
              </div>
            )
          }
        </div>
      </Box>
    </>
  )
}
